import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingBusLottie } from '@/components/ui/loading-bus-lottie';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireStaff?: boolean;
  requireCounterStaff?: boolean;
  requireFinance?: boolean;
  requireSupport?: boolean;
  requireHr?: boolean;
  requireBusStaff?: boolean;
  requireSupervisor?: boolean;
  /**
   * Allow any of these roles through. Use for surfaces shared by several
   * roles (e.g. the admin control panel, open to admin + supervisor +
   * finance + support + counter_staff, each seeing their own slice inside).
   * This is a route gate only — what each role can DO is enforced
   * server-side by RLS / RPC guards.
   */
  allowRoles?: string[];
}

export default function ProtectedRoute({ children, requireAdmin, requireStaff, requireCounterStaff, requireFinance, requireSupport, requireHr, requireBusStaff, requireSupervisor, allowRoles }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingBusLottie size="lg" label="Loading Star Line…" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requireAdmin && profile?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (allowRoles && !allowRoles.includes(profile?.role || '')) {
    return <Navigate to="/" replace />;
  }

  if (requireStaff && !['driver', 'supervisor', 'staff', 'admin'].includes(profile?.role || '')) {
    return <Navigate to="/login" replace />;
  }

  // A counter admin runs a terminal and may also work a counter, so they pass
  // the counter gate too. *Which* terminal they can act on is not decided here
  // — `counter_admin_may_manage()` enforces that server-side on every RPC.
  if (
    requireCounterStaff
    && !['counter_staff', 'counter_admin', 'admin'].includes(profile?.role || '')
  ) {
    return <Navigate to="/login" replace />;
  }

  if (requireFinance && profile?.role !== 'finance' && profile?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  if (requireSupport && profile?.role !== 'support' && profile?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  if (requireHr && profile?.role !== 'hr' && profile?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  if (requireBusStaff && !['staff', 'supervisor', 'admin'].includes(profile?.role || '')) {
    return <Navigate to="/login" replace />;
  }

  if (requireSupervisor && !['supervisor', 'admin'].includes(profile?.role || '')) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
